/**
 * @file ReceiptItemEditDialog.tsx
 * @description «Изменить позицию» строки черновика приёмки (phase19.31, Б16;
 * вопрос пользователя «как добавить ячейку в позицию, которая в таблице?»).
 *
 * Окно, а не режим правой панели: панель «Добавить строку» держит фокус
 * скана, и режим «правки» в ней ломал бы сценарий «скан → количество →
 * добавить». На телефоне окно — во весь экран (правило диалогов < sm).
 *
 * Поля — те же, что у панели добавления (`ReceiptItemLotFields`), заполнены
 * текущими значениями; фасовка показана, но не меняется. Цену продажи не
 * видит контекст с одними складскими правами (SC51; признак считает
 * страница через `canSeeSalePrice`). Цены правятся (контракт §15): неверную
 * цену ловим до отправки под полями цен, отказ сервера 422
 * `receipt_item_price_invalid` страница показывает его текстом
 * (`wmsErrorText`). Сохранение отправляет только изменённые поля
 * (`receiptItemEditPatch`).
 */

import { useState } from "react";

import {
  receiptItemEditInitial,
  receiptItemEditPatch,
  receiptItemPriceError,
  type ReceiptItemPatch,
} from "./receipt-item-edit";
import { ReceiptItemLotFields } from "./ReceiptItemLotFields";
import { useSkuExpiryPolicy } from "./useSkuExpiryPolicy";
import { useWarehouseBins } from "./useWarehouseBins";
import { WmsFieldLabel } from "./WmsFieldLabel";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { formatSkuName, type GoodsReceiptItemDto } from "../../lib/api-wms";
import { qtyInputProps, skuCountUnit, validateQty } from "../../lib/qty-input";

interface Props {
  item: GoodsReceiptItemDto;
  /** Склад документа — ячейки и скан адреса. */
  warehouseId: string;
  /** Показывать цену продажи — результат `canSeeSalePrice` (SC51, контракт §11). */
  showSalePrice: boolean;
  /** true — сохранено, окно закрывается. */
  onSubmit: (patch: ReceiptItemPatch) => Promise<boolean>;
  onClose: () => void;
}

export function ReceiptItemEditDialog({ item, warehouseId, showSalePrice, onSubmit, onClose }: Props) {
  const [value, setValue] = useState(() => receiptItemEditInitial(item));
  const [qtyError, setQtyError] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const bins = useWarehouseBins(warehouseId);
  const { policy } = useSkuExpiryPolicy(item.sku?.product.id);
  // У строки документа единица фасовки — объект `{ code, label }`; правилу счёта нужен код.
  const countUnit = skuCountUnit(item.sku ? { saleQuantity: item.sku.saleQuantity, saleUnit: item.sku.saleUnit?.code } : null);
  const patch = receiptItemEditPatch(item, value, { showSalePrice });
  const changed = Object.keys(patch).length > 0;

  async function save() {
    if (!changed || saving) return;
    if (patch.quantity !== undefined) {
      const message = validateQty(patch.quantity, countUnit, { allowZero: false });
      if (message) {
        setQtyError(message);
        return;
      }
    }
    // Та же проверка цены, что у сервера (422 `receipt_item_price_invalid`), — до отправки.
    const priceMessage =
      (patch.unitCost ? receiptItemPriceError(patch.unitCost) : null) ??
      (patch.salePrice ? receiptItemPriceError(patch.salePrice) : null);
    if (priceMessage) {
      setPriceError(priceMessage);
      return;
    }
    setSaving(true);
    const ok = await onSubmit(patch);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Изменить позицию</DialogTitle>
          <DialogDescription>
            {item.lineNo ? `${item.lineNo}. ` : ""}
            {formatSkuName(item.sku)} · <span className="font-mono">{item.sku?.code ?? item.skuId}</span>
          </DialogDescription>
        </DialogHeader>
        {/* Поля прокручиваются, полоса кнопок закреплена снизу (приёмка qa4
            п. 9: на 360 предупреждение о сроке пряталось под кнопками без
            намёка на прокрутку). `-mx-6 px-6` — рамка фокуса полей не
            срезается краем прокрутки; `pb-4` — последний блок не прилипает
            к полосе. */}
        <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 pb-4">
          <WmsFieldLabel label={`Количество, ${countUnit}`}>
            <input
              type="text"
              value={value.quantity}
              onChange={(e) => {
                setValue((prev) => ({ ...prev, quantity: e.target.value }));
                setQtyError(null);
              }}
              inputMode={qtyInputProps(countUnit).inputMode}
              className="min-h-[var(--control-h,3rem)] w-full rounded-lg bg-surface-lowest px-3 text-[length:var(--fs-field,1rem)] tabular-nums ring-ghost focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </WmsFieldLabel>
          {qtyError ? <p className="text-xs text-destructive">{qtyError}</p> : null}
          <ReceiptItemLotFields
            value={value.lot}
            onChange={(lot) => {
              setValue((prev) => ({ ...prev, lot }));
              setPriceError(null);
            }}
            warehouseId={warehouseId}
            bins={bins}
            expiryPolicy={policy}
            showSalePrice={showSalePrice}
            pricesError={priceError}
          />
        </div>
        <DialogFooter className="-mx-6 shrink-0 border-t border-[var(--border)] px-6 pt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void save()} disabled={!changed || saving} loading={saving}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
